import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './sic-combo-box.css.lit';
import { classMap } from 'lit/directives/class-map.js';
import { _dispatchComboSelectionEvent } from './utilities';
/**
 * The SicComboBox component
 * @element sic-combo-box
 */
@customElement('sic-combo-box')
export class SicComboBox extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  /**
   * Wether the panel is active/open.
   */
  @property({ type: Boolean, reflect: true, attribute: true })
  active = false;

  /**.
   * Controlled by parent dropdown-list component.
   */
  @property({ type: String, reflect: true, attribute: true })
  disable = 'false';

  /**
   * Initially the desired button text
   * changes to the text of the selected list element
   */
  @property({ type: String })
  label: string;

  /**
   * Retains the original label value. Automagically set.
   */
  @property({ type: String })
  labelStatic: string;

  /**
   * Visible question text rendered inside this component (e.g. "Role", "Topic").
   */
  @property({ type: String, attribute: 'combo-box-lable' })
  comboBoxLable: string;

  @property({ type: String, reflect: true })
  id: string = self.crypto.randomUUID
    ? self.crypto.randomUUID()
    : self.crypto.getRandomValues(new Uint32Array(3)).join('-');

  /**
   * Array of li text
   */
  @property({ type: Object })
  listData: Object;

  /**
   * ID of selected list item
   * emitted by _dispatchComboSelectionEvent
   */
  @property({ type: String, attribute: 'active-key', reflect: true })
  activeKey: string;

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('keydown', event => this.closeOnEsc(event));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('keydown', event => this.closeOnEsc(event));
  }

  firstUpdated(): void {
    this.labelStatic = this.label;
  }

  updated() {
    this.generateDropdownMarkup();
    // Called here because until the dropdown is open the list cannot be queried.
    this.applyTabOutListener();
    if (this.activeKey) {
      this.label = this.activeKey;
    } else {
      this.label = this.labelStatic;
    }
  }

  render(): TemplateResult {
    const labelText = this.label ? this.label : '--Select--';
    const labelId = `label-${this.id}`;
    const valueId = `value-${this.id}`;

    const buttonClasses = {
      'combo-box--button': true,
      'active': this.active,
      'inactive': !this.active,
      'disabled': this.disable == 'true',
    };

    const iconClasses = {
      'combo-box--icon': true,
      'active': this.active,
    };

    const overlayClasses = {
      'combo-box--overlay': true,
      'hidden': !this.active,
    };

    return html`
      <span class="combo-box--label" id="${labelId}">${this.comboBoxLable}</span>
      <div class="${classMap(overlayClasses)}" @click=${this.forceClose}></div>
      <section class="combo-box">
        <button
          class="${classMap(buttonClasses)}"
          role="combobox"
          id="button-${this.id}"
          aria-disabled="${this.disable}"
          aria-expanded="${this.active}"
          aria-haspopup="listbox"
          aria-controls="list-${this.id}"
          aria-labelledby="${labelId} ${valueId}"
          aria-activedescendant="${this.activeKey ? this.activeKey : null}"
          @click="${() => this.toggleActive()}"
          @keydown=${this.onButtonKeydown}
          tabindex=${this.disable == 'true' ? -1 : 0}
        >
          <span role="presentation" id="${valueId}">${labelText}</span>
          <span class="${classMap(iconClasses)}"> ${this.setIcon()} </span>
        </button>
        <ul class="combo-box--list" role="listbox" id="list-${this.id}">
          ${this.generateDropdownMarkup()}
        </ul>
      </section>
    `;
  }

  /**
   * If the dropown is active then make it in-active. Otherwise visa-versa.
   */
  toggleActive(): void {
    this.active = !this.active;
  }

  /**
   * Closes dropdown on Shift + Tab out of dropdown button.
   */
  handleTabOutFirst(event: KeyboardEvent) {
    if (event.key === 'Tab' && event.shiftKey) {
      this.active = false;
    }
  }

  onButtonKeydown = (event: KeyboardEvent) => {

    // Apply Shift+Tab close behavior.
    this.handleTabOutFirst(event);

    if (this.disable === 'true') return;

    const key = event.key;

    // Open on Enter/Space/ArrowDown (common combobox behavior)
    if ((key === 'Enter' || key === ' ') && !this.active) {
      event.preventDefault();
      this.active = true;
      // move focus to active/first option on next frame after render updates
      queueMicrotask(() => this.focusOption('activeOrFirst'));
      return;
    }

    if (key === 'ArrowDown' || key === 'ArrowUp') {
      // Prevent page scroll
      event.preventDefault();

      // Open if closed, then focus first/last option accordingly
      if (!this.active) {
        this.active = true;
        queueMicrotask(() => {
          this.focusOption(key === 'ArrowDown' ? 'first' : 'last');
        });
        return;
      }

      // If open, move within options
      queueMicrotask(() => this.moveFocusBy(key === 'ArrowDown' ? 1 : -1));
      return;
    }

    if (key === 'Escape' && this.active) {
      event.preventDefault();
      this.active = false;
      // keep focus on button
    }
  };

  getOptions(): HTMLElement[] {
    return Array.from(this.shadowRoot?.querySelectorAll('li[role="option"]') ?? []) as HTMLElement[];
  }

  getFocusedOptionIndex(options: HTMLElement[]): number {
    const activeEl = this.shadowRoot?.activeElement as HTMLElement | null;
    return activeEl ? options.indexOf(activeEl) : -1;
  }

  focusOption(which: 'first' | 'last' | 'activeOrFirst') {
    const options = this.getOptions();
    if (!options.length) return;

    let el: HTMLElement | undefined;

    if (which === 'first') el = options[0];
    if (which === 'last') el = options[options.length - 1];

    if (which === 'activeOrFirst') {
      el = this.activeKey
        ? (this.shadowRoot?.getElementById(this.activeKey) as HTMLElement | null) ?? options[0]
        : options[0];
    }

    el?.focus();
  }

  moveFocusBy(delta: number) {
    const options = this.getOptions();
    if (!options.length) return;

    let idx = this.getFocusedOptionIndex(options);

    // If nothing in list is focused, start at activeKey (or first)
    if (idx === -1) {
      const activeEl = this.activeKey
        ? (this.shadowRoot?.getElementById(this.activeKey) as HTMLElement | null)
        : null;
      idx = activeEl ? options.indexOf(activeEl) : 0;
    } else {
      idx = idx + delta;
    }

    // clamp
    idx = Math.max(0, Math.min(options.length - 1, idx));
    options[idx].focus();
  }

  /**
   * Closes dropdown if last element of list is forward Tabbed out.
   */
  handleTabOutLast(event: KeyboardEvent) {
    if (event.key === 'Tab' && !event.shiftKey) {
      this.active = false;
    }
  }

  /**
   * Adds a listener that closes the dropdown if the last item is tabbed out of.
   */
  applyTabOutListener() {
    const lastChildElement = this.getLastListItemChild();

    if (lastChildElement) {
      lastChildElement.addEventListener('keydown', event =>
        this.handleTabOutLast(event)
      );
    }
  }

  /**
   * Return the last <li>'s first child element.
   */
  getLastListItemChild() {
    const list = this.shadowRoot?.querySelectorAll('li');

    if (list?.length) {
      const lastEL = list[list.length - 1] as HTMLElement;
      return lastEL;
    } else {
      return false;
    }
  }
  /**
   * Closes the dropdown on Escape.
   */
  closeOnEsc(event: KeyboardEvent) {
    if (event.key !== 'Escape') {
      return;
    } else {
      this.active = false;
    }
  }

  setIcon() {
    if (this.active) {
      return html`<sic-icon
        name="chevron-up"
        size="20px"
        svg-fill="currentColor"
      ></sic-icon> `;
    } else {
      return html`<sic-icon
        name="chevron-down"
        size="20px"
        svg-fill="currentColor"
      ></sic-icon>`;
    }
  }

  forceClose() {
    this.active = false;
  }

  setActiveKey(e: Event) {
    if (!e.target) return;

    const target = e.target as HTMLElement;

    // Handle clicks
    if (e.type === 'click') {
      this.activeKey = target.id;
      this.label = target.id;
      _dispatchComboSelectionEvent(this);
      this.active = false;

      // Return focus to the button after selection
      queueMicrotask(() => (this.shadowRoot?.getElementById(`button-${this.id}`) as HTMLElement)?.focus());
      return;
    }

    if (e.type === 'keydown') {
      const ke = e as KeyboardEvent;

      // Prevent scroll for arrow keys
      if (ke.key === 'ArrowDown' || ke.key === 'ArrowUp') {
        ke.preventDefault();
        this.moveFocusBy(ke.key === 'ArrowDown' ? 1 : -1);
        return;
      }

      if (ke.key === 'Escape') {
        ke.preventDefault();
        this.active = false;
        queueMicrotask(() => (this.shadowRoot?.getElementById(`button-${this.id}`) as HTMLElement)?.focus());
        return;
      }

      if (ke.key === 'Enter' || ke.key === ' ') {
        ke.preventDefault();
        this.activeKey = target.id;
        this.label = target.id;
        _dispatchComboSelectionEvent(this);
        this.active = false;
        queueMicrotask(() => (this.shadowRoot?.getElementById(`button-${this.id}`) as HTMLElement)?.focus());
        return;
      }
    }
  }


  generateDropdownMarkup() {
    if (!this.listData) {
      // bail
      return;
    } else {
      return Object.keys(this.listData).map(item => {
        return html`
          <li
            role="option"
            id=${item}
            @keydown=${this.setActiveKey}
            @click=${this.setActiveKey}
            tabindex="-1"
            aria-selected="${this.activeKey === item}"
          >
            ${item}
          </li>
        `;
      });
    }
  }
}
